import { AppContext } from '@edx/frontend-platform/react';
import { getConfig, getQueryParameters } from '@edx/frontend-platform';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import memoize from 'memoize-one';
import findIndex from 'lodash.findindex';
import { sendTrackingLogEvent } from '@edx/frontend-platform/analytics';
import {
  injectIntl,
  intlShape,
  FormattedMessage,
  getCountryList,
  getLanguageList,
} from '@edx/frontend-platform/i18n';
import {
  Hyperlink, Icon, Alert,
} from '@openedx/paragon';
import { CheckCircle, Error, WarningFilled } from '@openedx/paragon/icons';

import messages from './AccountSettingsPage.messages';
import {
  fetchSettings,
  saveMultipleSettings,
  saveSettings,
  updateDraft,
  beginNameChange,
} from './data/actions';
import { accountSettingsPageSelector } from './data/selectors';
import PageLoading from './PageLoading';
import DeleteAccount from './delete-account';
import EditableField from './EditableField';
import ResetPassword from './reset-password';
import NameChange from './name-change';
import ThirdPartyAuth from './third-party-auth';
import BetaLanguageBanner from './BetaLanguageBanner';
import EmailField from './EmailField';
import OneTimeDismissibleAlert from './OneTimeDismissibleAlert';
import DOBModal from './DOBForm';
import {
  YEAR_OF_BIRTH_OPTIONS,
  EDUCATION_LEVELS,
  GENDER_OPTIONS,
  COUNTRY_WITH_STATES,
  COPPA_COMPLIANCE_YEAR,
  WORK_EXPERIENCE_OPTIONS,
  getStatesList,
} from './data/constants';
import { fetchSiteLanguages } from './site-language';
import { fetchCourseList } from '../notification-preferences/data/thunks';
import { withLocation, withNavigate } from './hoc';

// Fields with their own save path, handled outside saveMultipleSettings.
const SEPARATE_SAVES = ['siteLanguage', 'work_experience', 'useVerifiedNameForCerts'];

class AccountSettingsPage extends React.Component {
  constructor(props, context) {
    super(props, context);

    const duplicateTpaProvider = getQueryParameters().duplicate_provider;
    this.state = {
      duplicateTpaProvider,
    };

    this.navLinkRefs = {
      '#basic-information': React.createRef(),
      '#profile-information': React.createRef(),
      '#social-media': React.createRef(),
      '#site-preferences': React.createRef(),
      '#linked-accounts': React.createRef(),
      '#delete-account': React.createRef(),
    };
  }

  componentDidMount() {
    this.props.fetchCourseList();
    this.props.fetchSettings();
    this.props.fetchSiteLanguages(this.props.navigate);
    sendTrackingLogEvent('edx.user.settings.viewed', {
      page: 'account',
      visibility: null,
      user_id: this.context.authenticatedUser.userId,
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.loading && !prevProps.loaded && this.props.loaded) {
      const locationHash = global.location.hash;
      // Check for the locationHash in the URL and then scroll to it if it is in the
      // NavLinks list
      if (typeof locationHash !== 'string') {
        return;
      }
      if (Object.keys(this.navLinkRefs).includes(locationHash) && this.navLinkRefs[locationHash].current) {
        window.scrollTo(0, this.navLinkRefs[locationHash].current.offsetTop);
      }
    }
  }

  // NOTE: We need 'locale' for the memoization in getLocalizedTimeZoneOptions.  Don't remove it!
  // eslint-disable-next-line no-unused-vars
  getLocalizedTimeZoneOptions = memoize((timeZoneOptions, countryTimeZoneOptions, locale) => {
    const concatTimeZoneOptions = [{
      label: this.props.intl.formatMessage(messages['account.settings.field.time.zone.default']),
      value: '',
    }];
    if (countryTimeZoneOptions.length) {
      concatTimeZoneOptions.push({
        label: this.props.intl.formatMessage(messages['account.settings.field.time.zone.country']),
        group: countryTimeZoneOptions,
      });
    }
    concatTimeZoneOptions.push({
      label: this.props.intl.formatMessage(messages['account.settings.field.time.zone.all']),
      group: timeZoneOptions,
    });
    return concatTimeZoneOptions;
  });

  getLocalizedOptions = memoize((locale, country) => ({
    countryOptions: [{
      value: '',
      label: this.props.intl.formatMessage(messages['account.settings.field.country.options.empty']),
    }].concat(getCountryList(locale).map(({ code, name }) => ({ value: code, label: name }))),
    stateOptions: [{
      value: '',
      label: this.props.intl.formatMessage(messages['account.settings.field.state.options.empty']),
    }].concat(getStatesList(country)),
    languageProficiencyOptions: [{
      value: '',
      label: this.props.intl.formatMessage(messages['account.settings.field.language_proficiencies.options.empty']),
    }].concat(getLanguageList(locale).map(({ code, name }) => ({ value: code, label: name }))),
    yearOfBirthOptions: [{
      value: '',
      label: this.props.intl.formatMessage(messages['account.settings.field.year_of_birth.options.empty']),
    }].concat(YEAR_OF_BIRTH_OPTIONS),
    educationLevelOptions: EDUCATION_LEVELS.map(key => ({
      value: key,
      label: this.props.intl.formatMessage(messages[`account.settings.field.education.levels.${key || 'empty'}`]),
    })),
    genderOptions: GENDER_OPTIONS.map(key => ({
      value: key,
      label: this.props.intl.formatMessage(messages[`account.settings.field.gender.options.${key || 'empty'}`]),
    })),
    workExperienceOptions: WORK_EXPERIENCE_OPTIONS.map(key => ({
      value: key,
      label: key === '' ? this.props.intl.formatMessage(messages['account.settings.field.work.experience.options.empty']) : key,
    })),
  }));

  handleEditableFieldChange = (name, value) => {
    this.props.updateDraft(name, value);
  };

  handleSubmit = (formId, values) => {
    const { formValues } = this.props;
    let extendedProfileObject = {};

    if ('extended_profile' in formValues && formValues.extended_profile.some((field) => field.field_name === formId)) {
      extendedProfileObject = {
        extended_profile: formValues.extended_profile.map(field => (field.field_name === formId
          ? { ...field, field_value: values }
          : field)),
      };
    }
    this.props.saveSettings(formId, values, extendedProfileObject);
  };

  handleSubmitVerifiedName = (formId, values) => {
    if (Object.keys(this.props.drafts).includes('useVerifiedNameForCerts')) {
      this.props.saveSettings('useVerifiedNameForCerts', this.props.formValues.useVerifiedNameForCerts);
    }
    if (values !== this.props.committedValues?.verified_name) {
      this.props.beginNameChange(formId);
    } else {
      this.props.saveSettings(formId, values);
    }
  };

  handleDone = (event) => {
    event.preventDefault();
    const { drafts } = this.props;
    const changed = this.changedFields();
    const settings = changed
      .filter(name => !SEPARATE_SAVES.includes(name))
      // The time zone endpoint rejects an empty string; "default" is null.
      .map(formId => ({ formId, commitValues: formId === 'time_zone' ? (drafts[formId] || null) : drafts[formId] }));
    if (this.props.drafts && 'useVerifiedNameForCerts' in this.props.drafts
      && this.props.drafts.useVerifiedNameForCerts !== this.props.committedValues?.useVerifiedNameForCerts) {
      settings.push({ formId: 'useVerifiedNameForCerts', commitValues: this.props.formValues.useVerifiedNameForCerts });
    }
    if (settings.length) { this.props.saveMultipleSettings(settings); }
    if (changed.includes('work_experience')) { this.handleSubmit('work_experience', drafts.work_experience); }
    if (changed.includes('siteLanguage')) { this.props.saveSettings('siteLanguage', this.props.siteLanguage.draft); }
  };

  isEditable(fieldName) {
    return !this.props.staticFields.includes(fieldName);
  }

  isManagedProfile() {
    // Enterprise customer profiles are managed by their organizations. We determine whether
    // a profile is managed or not by the presence of the profileDataManager prop.
    return Boolean(this.props.profileDataManager);
  }

  isChanged(name) {
    const { drafts, committedValues, confirmationValues } = this.props;
    if (name === 'siteLanguage') {
      const { draft } = this.props.siteLanguage;
      return draft !== undefined && draft !== this.context.locale;
    }
    if (!drafts || !(name in drafts)) { return false; }
    // A changed email stays a draft until the learner confirms it; once the
    // confirmation mail is out it is not an unsaved change any more.
    if (name === 'email' && confirmationValues?.email === drafts.email) { return false; }
    if (name === 'work_experience') {
      const saved = committedValues?.extended_profile?.find(field => field.field_name === name)?.field_value;
      return String(drafts[name] ?? '') !== String(saved ?? '');
    }
    return String(drafts[name] ?? '') !== String(committedValues?.[name] ?? '');
  }

  changedFields() {
    const names = Object.keys(this.props.drafts || {});
    if (this.props.siteLanguage.draft !== undefined && !names.includes('siteLanguage')) { names.push('siteLanguage'); }
    return names.filter(name => name !== 'useVerifiedNameForCerts' && this.isChanged(name));
  }

  renderDuplicateTpaProviderMessage() {
    if (!this.state.duplicateTpaProvider) {
      return null;
    }

    // If there is a "duplicate_provider" query parameter, that's the backend's
    // way of telling us that the provider account the user tried to link is already linked
    // to another user account on the platform. We use this to display a message to that effect,
    // and remove the parameter from the URL.
    this.props.navigate(this.props.location, { replace: true });

    return (
      <div>
        <Alert variant="danger">
          <FormattedMessage
            id="account.settings.message.duplicate.tpa.provider"
            defaultMessage="The {provider} account you selected is already linked to another {siteName} account."
            description="alert message informing the user that the third-party account they attempted to link is already linked to another account"
            values={{
              provider: <b>{this.state.duplicateTpaProvider}</b>,
              siteName: getConfig().SITE_NAME,
            }}
          />
        </Alert>
      </div>
    );
  }

  renderManagedProfileMessage() {
    if (!this.isManagedProfile()) {
      return null;
    }

    return (
      <div>
        <Alert variant="info">
          <FormattedMessage
            id="account.settings.message.managed.settings"
            defaultMessage="Your profile settings are managed by {managerTitle}. Contact your administrator or {support} for help."
            description="alert message informing the user their account data is managed by a third party"
            values={{
              managerTitle: <b>{this.props.profileDataManager}</b>,
              support: (
                <Hyperlink destination={getConfig().SUPPORT_URL} target="_blank">
                  <FormattedMessage
                    id="account.settings.message.managed.settings.support"
                    defaultMessage="support"
                    description="website support"
                  />
                </Hyperlink>
              ),
            }}
          />
        </Alert>
      </div>
    );
  }

  renderFullNameHelpText = (status, proctoredExamId) => {
    if (!this.props.verifiedNameHistory) {
      return this.props.intl.formatMessage(messages['account.settings.field.full.name.help.text']);
    }

    let messageString = 'account.settings.field.full.name.help.text';

    if (status === 'submitted') {
      messageString += '.submitted';
      if (proctoredExamId) {
        messageString += '.proctored';
      }
    } else {
      messageString += '.default';
    }

    if (!this.props.committedValues.useVerifiedNameForCerts) {
      messageString += '.certificate';
    }

    return this.props.intl.formatMessage(messages[messageString]);
  };

  renderVerifiedNameSuccessMessage = (verifiedName, created) => {
    const dateValue = new Date(created).valueOf();
    const id = `dismissedVerifiedNameSuccessMessage-${verifiedName}-${dateValue}`;

    return (
      <OneTimeDismissibleAlert
        id={id}
        variant="success"
        icon={CheckCircle}
        header={this.props.intl.formatMessage(messages['account.settings.field.name.verified.success.message.header'])}
        body={this.props.intl.formatMessage(messages['account.settings.field.name.verified.success.message'])}
      />
    );
  };

  renderVerifiedNameFailureMessage = (verifiedName, created) => {
    const dateValue = new Date(created).valueOf();
    const id = `dismissedVerifiedNameFailureMessage-${verifiedName}-${dateValue}`;

    return (
      <OneTimeDismissibleAlert
        id={id}
        variant="danger"
        icon={Error}
        header={this.props.intl.formatMessage(messages['account.settings.field.name.verified.failure.message.header'])}
        body={
          (
            <div className="d-flex flex-row">
              {this.props.intl.formatMessage(messages['account.settings.field.name.verified.failure.message'])}
            </div>
          )
        }
      />
    );
  };

  renderVerifiedNameSubmittedMessage = (willCertNameChange) => (
    <Alert
      variant="warning"
      icon={WarningFilled}
    >
      <Alert.Heading>
        {this.props.intl.formatMessage(messages['account.settings.field.name.verified.submitted.message.header'])}
      </Alert.Heading>
      <p>
        {this.props.intl.formatMessage(messages['account.settings.field.name.verified.submitted.message'])}{' '}
        {
          willCertNameChange
          && this.props.intl.formatMessage(messages['account.settings.field.name.verified.submitted.message.certificate'])
        }
      </p>
    </Alert>
  );

  renderVerifiedNameMessage = verifiedNameRecord => {
    const {
      created,
      status,
      profile_name: profileName,
      verified_name: verifiedName,
      proctored_exam_attempt_id: proctoredExamId,
    } = verifiedNameRecord;
    let willCertNameChange = false;

    if (
      (
        // User submitted a profile name change, and uses their profile name on certificates
        this.props.committedValues.name !== profileName
        && !this.props.committedValues.useVerifiedNameForCerts
      )
      || (
        // User submitted a verified name change, and uses their verified name on certificates
        this.props.committedValues.name === profileName
        && this.props.committedValues.useVerifiedNameForCerts
      )
    ) {
      willCertNameChange = true;
    }

    if (proctoredExamId) {
      return null;
    }

    switch (status) {
      case 'approved':
        return this.renderVerifiedNameSuccessMessage(verifiedName, created);
      case 'denied':
        return this.renderVerifiedNameFailureMessage(verifiedName, created);
      case 'submitted':
        return this.renderVerifiedNameSubmittedMessage(willCertNameChange);
      default:
        return null;
    }
  };

  renderVerifiedNameIcon = (status) => {
    switch (status) {
      case 'approved':
        return (<Icon src={CheckCircle} className="ml-1" style={{ height: '18px', width: '18px', color: 'green' }} />);
      case 'submitted':
        return (<Icon src={WarningFilled} className="ml-1" style={{ height: '18px', width: '18px', color: 'yellow' }} />);
      default:
        return null;
    }
  };

  renderVerifiedNameHelpText = (status, proctoredExamId) => {
    let messageStr = 'account.settings.field.name.verified.help.text';

    // add additional string based on status
    if (status === 'approved') {
      messageStr += '.verified';
    } else if (status === 'submitted') {
      messageStr += '.submitted';
    } else {
      return null;
    }

    // add additional string if verified name came from a proctored exam attempt
    if (proctoredExamId) {
      messageStr += '.proctored';
    }

    // add additional string based on certificate name use
    if (this.props.committedValues.useVerifiedNameForCerts) {
      messageStr += '.certificate';
    }

    return this.props.intl.formatMessage(messages[messageStr]);
  };

  renderNameChangeModal() {
    if (this.props.nameChangeModal && this.props.nameChangeModal.formId) {
      return <NameChange targetFormId={this.props.nameChangeModal.formId} />;
    }
    return null;
  }

  renderSecondaryEmailField(editableFieldProps) {
    if (!this.props.formValues.secondary_email_enabled) {
      return null;
    }

    return (
      <EmailField
        name="secondary_email"
        label={this.props.intl.formatMessage(messages['account.settings.field.secondary.email'])}
        emptyLabel={this.props.intl.formatMessage(messages['account.settings.field.secondary.email.empty'])}
        value={this.props.formValues.secondary_email}
        confirmationMessageDefinition={messages['account.settings.field.secondary.email.confirmation']}
        {...editableFieldProps}
      />
    );
  }

  // ---- Layout: profile card + always-open form with one Done button ----
  //
  // Matches the house mockup: the fields are plain inputs bound to the same
  // redux drafts the per-field editors used (updateDraft), and Done saves every
  // changed field through the existing actions, so the name-verification modal,
  // the email confirmation and the site-language reload all still happen.

  renderFieldShell(name, label, control, help) {
    const id = `account-field-${name}`;
    const error = this.props.formErrors?.[name];
    return (
      <div className={`acct-field${error ? ' has-error' : ''}`} key={name}>
        <label className="acct-label" htmlFor={id}>{label}</label>
        <div className="acct-field__row">
          {control(id, error)}
          {help ? <div className="acct-help" id={`${id}-help`}>{help}</div> : null}
        </div>
        {error ? <p className="acct-error" role="alert">{error}</p> : null}
      </div>
    );
  }

  renderInput(name, label, {
    help, type = 'text', disabled = false, placeholder, value,
  } = {}) {
    const current = value !== undefined ? value : this.props.formValues[name];
    return this.renderFieldShell(name, label, (id, error) => (
      <input
        id={id}
        name={name}
        type={type}
        className="acct-input"
        value={current ?? ''}
        disabled={disabled}
        placeholder={placeholder}
        aria-describedby={help ? `${id}-help` : undefined}
        aria-invalid={error ? true : undefined}
        onChange={(e) => this.handleEditableFieldChange(name, e.target.value)}
      />
    ), help);
  }

  renderSelect(name, label, options, {
    help, disabled = false, value, wide = false,
  } = {}) {
    const current = value !== undefined ? value : this.props.formValues[name];
    const renderOption = option => (
      <option key={`${option.value}`} value={option.value ?? ''}>{option.label}</option>
    );
    return this.renderFieldShell(name, label, (id, error) => (
      <select
        id={id}
        name={name}
        className={`acct-input acct-select${wide ? ' acct-input--wide' : ''}`}
        value={current ?? ''}
        disabled={disabled}
        aria-describedby={help ? `${id}-help` : undefined}
        aria-invalid={error ? true : undefined}
        onChange={(e) => this.handleEditableFieldChange(name, e.target.value)}
      >
        {options.map(option => (option.group
          ? <optgroup key={option.label} label={option.label}>{option.group.map(renderOption)}</optgroup>
          : renderOption(option)))}
      </select>
    ), help);
  }

  renderProfileCard(countryOptions) {
    const { committedValues, siteLanguageOptions, intl } = this.props;
    const username = committedValues?.username || this.props.formValues.username;
    const displayName = committedValues?.name || username;
    const image = committedValues?.profile_image;
    const country = countryOptions.find(option => option.value === committedValues?.country && option.value)?.label;
    const language = siteLanguageOptions?.find(option => option.value === this.context.locale)?.label
      || this.context.locale;
    const notSet = intl.formatMessage(messages['account.settings.layout.not.set']);
    return (
      <div className="acct-card">
        <div className="acct-card__avatar">
          {image?.has_image
            ? <img src={image.image_url_large || image.image_url_medium} alt="" />
            : <span aria-hidden="true">{(displayName || '?').charAt(0).toUpperCase()}</span>}
        </div>
        <p className="acct-card__name">{displayName}</p>
        <p className="acct-card__username">@{username}</p>
        <dl className="acct-card__facts">
          <dt>{intl.formatMessage(messages['account.settings.layout.location'])}</dt>
          <dd>{country || notSet}</dd>
          <dt>{intl.formatMessage(messages['account.settings.layout.language'])}</dt>
          <dd>{language}</dd>
        </dl>
      </div>
    );
  }

  renderDoneBar() {
    const { intl, saveState } = this.props;
    const unsaved = this.changedFields().length > 0;
    let status = null;
    if (saveState === 'pending') {
      status = <span className="acct-status">{intl.formatMessage(messages['account.settings.layout.saving'])}</span>;
    } else if (saveState === 'error') {
      status = (
        <span className="acct-status acct-status--error">
          {intl.formatMessage(messages['account.settings.layout.save.error'])}
        </span>
      );
    } else if (unsaved) {
      status = <span className="acct-status">{intl.formatMessage(messages['account.settings.layout.unsaved'])}</span>;
    } else if (saveState === 'complete') {
      status = <span className="acct-status acct-status--ok">{intl.formatMessage(messages['account.settings.layout.saved'])}</span>;
    }
    return (
      <div className="acct-done">
        <button type="submit" className="acct-btn" disabled={!unsaved || saveState === 'pending'}>
          {intl.formatMessage(messages['account.settings.layout.done'])}
        </button>
        <div aria-live="polite">{status}</div>
      </div>
    );
  }

  renderContent() {
    const { intl, formValues } = this.props;
    const t = (id, values) => intl.formatMessage(messages[id], values);

    const {
      countryOptions,
      stateOptions,
      languageProficiencyOptions,
      yearOfBirthOptions,
      educationLevelOptions,
      genderOptions,
      workExperienceOptions,
    } = this.getLocalizedOptions(this.context.locale, formValues.country);

    // Show State field only if the country is US (could include Canada later)
    const showState = formValues.country === COUNTRY_WITH_STATES;
    const { verifiedName } = this.props;
    const hasWorkExperience = !!formValues?.extended_profile?.find(field => field.field_name === 'work_experience');
    const timeZoneOptions = this.getLocalizedTimeZoneOptions(
      this.props.timeZoneOptions,
      this.props.countryTimeZoneOptions,
      this.context.locale,
    );
    const hasLinkedTPA = findIndex(this.props.tpaProviders, provider => provider.connected) >= 0;
    const siteName = getConfig().SITE_NAME;
    const pendingEmail = this.props.confirmationValues?.email;

    // if user is under 13 and does not have cookie set
    const shouldUpdateDOB = (
      getConfig().ENABLE_COPPA_COMPLIANCE
      && getConfig().ENABLE_DOB_UPDATE
      && formValues.year_of_birth.toString() >= COPPA_COMPLIANCE_YEAR.toString()
      && !localStorage.getItem('submittedDOB')
    );
    const editableFieldProps = {
      onChange: this.handleEditableFieldChange,
      onSubmit: this.handleSubmit,
    };

    return (
      <div className="acct-layout">
        <aside className="acct-side">
          <h1 className="acct-title">{t('account.settings.layout.title')}</h1>
          {this.renderProfileCard(countryOptions)}
        </aside>

        <div className="acct-main">
          {shouldUpdateDOB && <DOBModal {...editableFieldProps} />}
          {this.props.mostRecentVerifiedName && this.renderVerifiedNameMessage(this.props.mostRecentVerifiedName)}
          {localStorage.getItem('submittedDOB') && (
            <OneTimeDismissibleAlert
              id="updated-dob"
              variant="success"
              icon={CheckCircle}
              header={t('account.settings.field.dob.form.success')}
              body=""
            />
          )}
          {this.renderManagedProfileMessage()}
          {this.renderNameChangeModal()}

          <form className="acct-form" onSubmit={this.handleDone} noValidate>
            <h2 className="acct-heading" id="basic-information" ref={this.navLinkRefs['#basic-information']}>
              {t('account.settings.layout.basic')}
            </h2>
            <section className="acct-panel" aria-labelledby="basic-information">
              <BetaLanguageBanner />
              {this.renderInput('username', t('account.settings.field.username'), {
                disabled: true,
                help: t('account.settings.field.username.help.text', { siteName }),
              })}
              {this.renderInput('name', t('account.settings.layout.field.name'), {
                disabled: verifiedName
                  ? !(this.isEditable('verifiedName') && this.isEditable('name'))
                  : !this.isEditable('name'),
                value: verifiedName?.status === 'submitted' && formValues.pending_name_change
                  ? formValues.pending_name_change
                  : formValues.name,
                help: verifiedName
                  ? this.renderFullNameHelpText(verifiedName.status, verifiedName.proctored_exam_attempt_id)
                  : t('account.settings.field.full.name.help.text'),
              })}
              {verifiedName && (
                <EditableField
                  name="verified_name"
                  type="text"
                  value={formValues.verified_name}
                  label={(
                    <div className="d-flex">
                      {t('account.settings.field.name.verified')}
                      {this.renderVerifiedNameIcon(verifiedName.status)}
                    </div>
                  )}
                  helpText={this.renderVerifiedNameHelpText(
                    verifiedName.status,
                    verifiedName.proctored_exam_attempt_id,
                  )}
                  isEditable={this.isEditable('verifiedName')}
                  isGrayedOut={!this.isEditable('verifiedName')}
                  onChange={this.handleEditableFieldChange}
                  onSubmit={this.handleSubmitVerifiedName}
                />
              )}
              {this.renderInput('email', t('account.settings.field.email'), {
                type: 'email',
                disabled: !this.isEditable('email'),
                help: t('account.settings.field.email.help.text', { siteName }),
              })}
              {pendingEmail && (
                <p className="acct-note">{t('account.settings.layout.email.pending', { email: pendingEmail })}</p>
              )}
              {this.renderSecondaryEmailField(editableFieldProps)}
              <div className="acct-password">
                <ResetPassword email={formValues.email} />
              </div>
              {this.renderSelect(
                'siteLanguage',
                t('account.settings.layout.field.site.language'),
                this.props.siteLanguageOptions,
                {
                  value: this.props.siteLanguage.draft ?? this.context.locale,
                  help: t('account.settings.layout.help.site.language'),
                },
              )}
              {this.renderInput('mailing_address', t('account.settings.layout.field.mailing.address'))}
              {this.renderInput('city', t('account.settings.layout.field.city'))}
              {this.renderSelect('country', t('account.settings.layout.field.country'), countryOptions, {
                wide: true, disabled: !this.isEditable('country'),
              })}
              {showState && this.renderSelect('state', t('account.settings.layout.field.state'), stateOptions, {
                wide: true, disabled: !this.isEditable('state'),
              })}
              {this.renderSelect('time_zone', t('account.settings.layout.field.time.zone'), timeZoneOptions, {
                wide: true, help: t('account.settings.field.time.zone.description'),
              })}
              {this.renderDoneBar()}
            </section>

            <section className="acct-panel acct-panel--split">
              <div className="acct-col" id="profile-information" ref={this.navLinkRefs['#profile-information']}>
                <h2 className="acct-subheading">{t('account.settings.layout.additional')}</h2>
                {this.renderSelect(
                  'level_of_education',
                  t('account.settings.layout.field.education'),
                  getConfig().ENABLE_COPPA_COMPLIANCE
                    ? educationLevelOptions.filter(option => option.value !== 'el')
                    : educationLevelOptions,
                )}
                {this.renderSelect('gender', t('account.settings.layout.field.gender'), genderOptions)}
                {!getConfig().ENABLE_COPPA_COMPLIANCE
                  && this.renderSelect('year_of_birth', t('account.settings.layout.field.year.of.birth'), yearOfBirthOptions)}
                {hasWorkExperience && this.renderSelect(
                  'work_experience',
                  t('account.settings.field.work.experience'),
                  workExperienceOptions,
                  {
                    value: this.props.drafts?.work_experience
                      ?? formValues?.extended_profile?.find(field => field.field_name === 'work_experience')?.field_value,
                  },
                )}
                {this.renderSelect('language_proficiencies', t('account.settings.layout.field.preferred.language'), languageProficiencyOptions)}
              </div>
              <div className="acct-col" id="social-media">
                <h2 className="acct-subheading">{t('account.settings.layout.social')}</h2>
                {this.renderInput('social_link_facebook', t('account.settings.layout.field.facebook'), {
                  placeholder: 'https://www.facebook.com/username',
                  help: t('account.settings.layout.help.facebook'),
                })}
                {this.renderInput('social_link_twitter', t('account.settings.layout.field.twitter'), {
                  placeholder: 'https://www.twitter.com/username',
                  help: t('account.settings.layout.help.twitter'),
                })}
                {this.renderInput('social_link_linkedin', t('account.settings.layout.field.linkedin'), {
                  placeholder: 'https://www.linkedin.com/in/username',
                  help: t('account.settings.layout.help.linkedin'),
                })}
              </div>
              {/* The Done above saves these too; a second one here so nobody has
                  to scroll back up to find it. */}
              <div className="acct-col acct-col--full">{this.renderDoneBar()}</div>
            </section>
          </form>

          {/* Linked accounts only when the site offers a provider to link;
              otherwise it is a heading over "nothing to link". */}
          {this.props.tpaProviders?.length > 0 && (
            <section className="acct-panel" id="linked-accounts" ref={this.navLinkRefs['#linked-accounts']}>
              <h2 className="acct-subheading">{t('account.settings.section.linked.accounts')}</h2>
              <p>{t('account.settings.section.linked.accounts.description', { siteName })}</p>
              <ThirdPartyAuth />
            </section>
          )}

          {getConfig().ENABLE_ACCOUNT_DELETION && (
            <section className="acct-delete" id="delete-account" ref={this.navLinkRefs['#delete-account']}>
              <DeleteAccount isVerifiedAccount={this.props.isActive} hasLinkedTPA={hasLinkedTPA} />
            </section>
          )}
        </div>
      </div>
    );
  }

  renderError() {
    return (
      <div>
        {this.props.intl.formatMessage(messages['account.settings.loading.error'], {
          error: this.props.loadingError,
        })}
      </div>
    );
  }

  renderLoading() {
    return (
      <PageLoading srMessage={this.props.intl.formatMessage(messages['account.settings.loading.message'])} />
    );
  }

  render() {
    const {
      loading,
      loaded,
      loadingError,
    } = this.props;

    return (
      <div className="page__account-settings acct-page">
        {this.renderDuplicateTpaProviderMessage()}
        {loading ? this.renderLoading() : null}
        {loaded ? this.renderContent() : null}
        {loadingError ? this.renderError() : null}
      </div>
    );
  }
}

AccountSettingsPage.contextType = AppContext;

AccountSettingsPage.propTypes = {
  intl: intlShape.isRequired,
  loading: PropTypes.bool,
  loaded: PropTypes.bool,
  loadingError: PropTypes.string,

  // Form data
  formValues: PropTypes.shape({
    username: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    secondary_email: PropTypes.string,
    secondary_email_enabled: PropTypes.bool,
    year_of_birth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    country: PropTypes.string,
    level_of_education: PropTypes.string,
    gender: PropTypes.string,
    extended_profile: PropTypes.string,
    language_proficiencies: PropTypes.string,
    pending_name_change: PropTypes.string,
    phone_number: PropTypes.string,
    social_link_linkedin: PropTypes.string,
    social_link_facebook: PropTypes.string,
    social_link_twitter: PropTypes.string,
    time_zone: PropTypes.string,
    state: PropTypes.string,
    useVerifiedNameForCerts: PropTypes.bool.isRequired,
    verified_name: PropTypes.string,
  }).isRequired,
  committedValues: PropTypes.shape({
    name: PropTypes.string,
    username: PropTypes.string,
    country: PropTypes.string,
    profile_image: PropTypes.shape({
      has_image: PropTypes.bool,
      image_url_large: PropTypes.string,
      image_url_medium: PropTypes.string,
    }),
    extended_profile: PropTypes.arrayOf(PropTypes.shape({})),
    useVerifiedNameForCerts: PropTypes.bool,
    verified_name: PropTypes.string,
  }),
  drafts: PropTypes.shape({
    email: PropTypes.string,
    work_experience: PropTypes.string,
    useVerifiedNameForCerts: PropTypes.bool,
  }),
  saveState: PropTypes.string,
  confirmationValues: PropTypes.shape({ email: PropTypes.string }),
  formErrors: PropTypes.shape({
    name: PropTypes.string,
  }),
  siteLanguage: PropTypes.shape({
    previousValue: PropTypes.string,
    draft: PropTypes.string,
  }),
  siteLanguageOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  profileDataManager: PropTypes.string,
  staticFields: PropTypes.arrayOf(PropTypes.string),
  isActive: PropTypes.bool,
  secondary_email_enabled: PropTypes.bool,

  timeZoneOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  countryTimeZoneOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  })),
  fetchSiteLanguages: PropTypes.func.isRequired,
  updateDraft: PropTypes.func.isRequired,
  saveMultipleSettings: PropTypes.func.isRequired,
  saveSettings: PropTypes.func.isRequired,
  fetchSettings: PropTypes.func.isRequired,
  beginNameChange: PropTypes.func.isRequired,
  fetchCourseList: PropTypes.func.isRequired,
  tpaProviders: PropTypes.arrayOf(PropTypes.shape({
    connected: PropTypes.bool,
  })),
  nameChangeModal: PropTypes.shape({
    formId: PropTypes.string,
  }),
  verifiedName: PropTypes.shape({
    verified_name: PropTypes.string,
    status: PropTypes.string,
    proctored_exam_attempt_id: PropTypes.number,
  }),
  mostRecentVerifiedName: PropTypes.shape({
    verified_name: PropTypes.string,
    status: PropTypes.string,
    proctored_exam_attempt_id: PropTypes.number,
  }),
  verifiedNameHistory: PropTypes.arrayOf(
    PropTypes.shape({
      verified_name: PropTypes.string,
      status: PropTypes.string,
      proctored_exam_attempt_id: PropTypes.number,
    }),
  ),
  navigate: PropTypes.func.isRequired,
  location: PropTypes.string.isRequired,
};

AccountSettingsPage.defaultProps = {
  saveState: null,
  confirmationValues: {},
  loading: false,
  loaded: false,
  loadingError: null,
  committedValues: {
    useVerifiedNameForCerts: false,
    verified_name: null,
  },
  drafts: {},
  formErrors: {},
  siteLanguage: null,
  siteLanguageOptions: [],
  timeZoneOptions: [],
  countryTimeZoneOptions: [],
  profileDataManager: null,
  staticFields: [],
  tpaProviders: [],
  isActive: true,
  secondary_email_enabled: false,
  nameChangeModal: {},
  verifiedName: null,
  mostRecentVerifiedName: {},
  verifiedNameHistory: [],
};

export default withLocation(withNavigate(connect(accountSettingsPageSelector, {
  fetchCourseList,
  fetchSettings,
  saveSettings,
  saveMultipleSettings,
  updateDraft,
  fetchSiteLanguages,
  beginNameChange,
})(injectIntl(AccountSettingsPage))));
